import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faX, faY } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Map from 'react-map-gl/mapbox';
import { useParams } from 'react-router-dom';
import * as z from 'zod';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useDisableParkingCashCollection } from 'src/features/cashCollections/hooks/useDisableParkingCashCollection';
import { useEnableParkingCashCollection } from 'src/features/cashCollections/hooks/useEnableParkingCashCollection';
import {
    GET_CASH_COLLECTION_STATUS_RESOURCE,
    usGetCashCollectionStatus,
} from 'src/features/cashCollections/hooks/useGetCashCollectionStatus';
import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { useGetCityList } from 'src/features/parkingManagement/api/useGetCityList';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useUpdateParkingFormData } from 'src/features/parkingManagement/api/useUpdateParkingFormData';
import { ImageUpload } from 'src/features/parkingManagement/components/ImageUpload';
import { type City, GEO_TYPES } from 'src/features/parkingManagement/types/parking';
import { geoTypeOptions } from 'src/features/parkingManagement/utils';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils/messages';
import { CURRENCIES } from 'src/features/payStations/types';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { membershipRoleOptions } from 'src/features/userManagement/utils';
import { queryClient } from 'src/lib/react-query';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { InputWithIcon } from 'src/registry/components/inputs/InputWithIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { useTitleStore } from 'src/stores/useTitleStore';
import { mapErrors } from 'src/utils/errors';
import { generalErrorMessages, generalLabelMessages, generalMessages } from 'src/utils/messages';

type FormValues = z.infer<typeof formSchema>;
type NonBooleanFormFieldName = Omit<
    FormValues,
    'is_public' | 'is_featured' | 'enable_online_reservations' | 'enable_online_payments'
>;
type FormFieldName = keyof NonBooleanFormFieldName;

const formSchema = z.object({
    name: z.string().min(1),
    address: z.string().min(1),
    system_id: z.string(),
    city: z.string().optional().nullable(),
    geo_type: z.nativeEnum(GEO_TYPES),
    longitude: z.string().refine((val) => {
        const num = Number(val);
        return !isNaN(num) && num >= -180 && num <= 180;
    }),
    latitude: z.string().refine((val) => {
        const num = Number(val);
        return !isNaN(num) && num >= -90 && num <= 90;
    }),
    is_public: z.boolean(),
    is_featured: z.boolean(),
    enable_online_reservations: z.boolean(),
    enable_online_payments: z.boolean(),
    default_currency: z.nativeEnum(CURRENCIES),
    photo: z
        .union([z.instanceof(File), z.string()])
        .optional()
        .nullable(),
    roles: z.array(
        z.enum([
            MEMBERSHIP_ROLES.CLIENT,
            MEMBERSHIP_ROLES.VALIDATOR,
            MEMBERSHIP_ROLES.OWNER,
            MEMBERSHIP_ROLES.FINANCE,
            MEMBERSHIP_ROLES.VIEWER,
            MEMBERSHIP_ROLES.OPERATOR,
            MEMBERSHIP_ROLES.PARKING_MANAGER,
            MEMBERSHIP_ROLES.RECEPTION,
            MEMBERSHIP_ROLES.SALES_MANAGER,
            MEMBERSHIP_ROLES.RECEPTION_MANAGER,
            MEMBERSHIP_ROLES.UBB_RESERVATIONS,
        ]),
    ),
    polygon: z.any().optional().nullable(),
    area_color: z.string().optional().nullable(),
});

const MAP_BOX_ACCESS_TOKEN = import.meta.env.VITE_MAP_BOX_ACCESS_TOKEN as string;

const GeneralSettings = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const mapRef = React.useRef<any>(null);
    const drawRef = React.useRef<any>(null);

    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });
    const { setTitle, removeTitle } = useTitleStore();
    const { data: cashStatus, isRefetching: isStatusLoading } = usGetCashCollectionStatus({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });

    const { mutate: enableCashCollection } = useEnableParkingCashCollection({
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [GET_CASH_COLLECTION_STATUS_RESOURCE] }),
    });
    const { mutate: disableCashCollection } = useDisableParkingCashCollection({
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [GET_CASH_COLLECTION_STATUS_RESOURCE] }),
    });
    const [newCollectionStatus, setNewCollectionStatus] = React.useState<null | string>(null);

    React.useEffect(() => {
        if (cashStatus) setNewCollectionStatus(cashStatus.is_enabled.toString());
    }, [cashStatus]);

    const { data: cityList } = useGetCityList({});

    const defaultValues = {
        name: data?.name ?? '',
        address: data?.address ?? '',
        system_id: data?.system_id ?? '',
        city: data?.city ?? '',
        geo_type: data?.geo_type ?? GEO_TYPES.POINT,
        longitude: data?.coordinates?.coordinates[0]?.toString() ?? '',
        latitude: data?.coordinates?.coordinates[1]?.toString() ?? '',
        polygon: data?.polygon
            ? {
                  type: 'MultiPolygon',
                  coordinates: data.polygon.coordinates,
              }
            : '',
        area_color: data?.area_color ?? '#0086C9',
        is_public: !!data?.is_public,
        is_featured: !!data?.is_featured,
        enable_online_reservations: !!data?.enable_online_reservations,
        enable_online_payments: !!data?.enable_online_payments,
        photo: null,
        photoPreview: data?.photo ?? '',
        default_currency: data?.default_currency ?? CURRENCIES.BGN,
        roles: data?.roles ?? [],
    };

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues,
    });

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        form.reset(defaultValues);
        return () => {
            removeTitle();
            form.reset();
        };
    }, [data, formatMessage, cityList]);

    const parkingSelectMapping = {
        [formatMessage(generalMessages.yes)]: 'true',
        [formatMessage(generalMessages.no)]: 'false',
    };

    const reservationsPaymentsSelectMapping = {
        [formatMessage(parkingManagementMessages.enabled)]: 'true',
        [formatMessage(parkingManagementMessages.disabled)]: 'false',
    };

    const {
        formState: { isDirty },
    } = form;

    const { mutate, isPending } = useUpdateParkingFormData({
        onSuccess: () => {
            toast({
                title: formatMessage(parkingManagementMessages.parkingChangeSuccess),
                description: formatMessage(parkingManagementMessages.parkingChangeSuccessDetails, { name: data?.name }),
            });
        },
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            } else if (e?.response?.data) {
                mapErrors(e.response.data, form.setError);
            }
        },
    });

    const onSubmit: SubmitHandler<FormValues> = (values) => {
        const formData = new FormData();

        if (!parkingId) {
            throw new Error('parkingId is required');
        }

        formData.append('parkingId', parkingId);
        formData.append('name', values.name);
        formData.append('address', values.address);
        formData.append('system_id', values.system_id);
        formData.append('city', values.city ?? '');
        formData.append('geo_type', values.geo_type ?? '');
        formData.append('is_public', String(values.is_public));
        formData.append('is_featured', String(values.is_featured));
        formData.append('enable_online_reservations', String(values.enable_online_reservations));
        formData.append('enable_online_payments', String(values.enable_online_payments));
        formData.append('default_currency', values.default_currency ?? '');
        formData.append('roles', JSON.stringify(values.roles));

        const longitude = Number(values.longitude);
        const latitude = Number(values.latitude);
        if (isNaN(longitude) || isNaN(latitude)) {
            throw new Error(formatMessage(parkingManagementMessages.invalidCoordinates));
        }
        if (values.geo_type === GEO_TYPES.POINT) {
            formData.append('coordinates', JSON.stringify({ type: 'Point', coordinates: [longitude, latitude] }));
        } else if (values.geo_type === GEO_TYPES.AREA && values.polygon) {
            let polygonCoordinates;
            if (!Array.isArray(values.polygon)) {
                polygonCoordinates = values.polygon.coordinates.map((coords: any) => ({
                    type: 'Polygon',
                    coordinates: coords,
                }));
            } else polygonCoordinates = values.polygon;

            const coordinates = polygonCoordinates.map((poly: any) => poly.coordinates);
            formData.append('polygon', JSON.stringify({ type: 'MultiPolygon', coordinates }));
            formData.append('area_color', values.area_color || defaultValues.area_color);
        }

        if (values.photo) {
            if (values.photo instanceof File) {
                formData.append('photo', values.photo);
            } else {
                const [header, base64] = values.photo.split(',');
                const mime = header.match(/data:(.*);base64/)?.[1] ?? 'image/jpeg';
                const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
                formData.append('photo', new File([bytes], 'photo', { type: mime }));
            }
        }
        if (newCollectionStatus !== cashStatus?.is_enabled?.toString()) {
            if (newCollectionStatus === 'true') enableCashCollection({ parkingId });
            else disableCashCollection({ parkingId });
        }
        mutate(formData);
        form.reset(values);
    };

    const rolesList: MEMBERSHIP_ROLES[] = Object.values(MEMBERSHIP_ROLES);

    const updatePolygonColors = React.useCallback((color: string) => {
        if (!mapRef.current || !drawRef.current) return;

        const map = mapRef.current;

        const updateLayers = () => {
            if (map.getLayer('gl-draw-polygon-fill.cold')) {
                map.setPaintProperty('gl-draw-polygon-fill.cold', 'fill-color', color);
                map.setPaintProperty('gl-draw-polygon-fill.cold', 'fill-opacity', 0.5);
            }
            if (map.getLayer('gl-draw-lines.cold')) {
                map.setPaintProperty('gl-draw-lines.cold', 'line-color', color);
            }
        };

        if (map.isStyleLoaded()) {
            updateLayers();
        } else {
            map.once('idle', updateLayers);
        }
    }, []);

    const getFormFieldBlock = (
        formFieldName: FormFieldName,
        formFieldBlockName: { id: string; defaultMessage: string },
    ) => {
        return (
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(formFieldBlockName)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name={formFieldName}
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input
                                        type="text"
                                        {...field}
                                        value={typeof field.value === 'string' ? field.value : ''}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
        );
    };

    const getFormFieldSelect = (
        formFieldName: keyof FormValues,
        formFieldBlockName: { id: string; defaultMessage: string },
        selectOptions: Record<string, string>,
    ) => {
        return (
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(formFieldBlockName)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name={formFieldName}
                        render={() => (
                            <FormItem>
                                <FormControl>
                                    <Select
                                        onValueChange={(val) =>
                                            form.setValue(formFieldName, val === 'true', {
                                                shouldDirty: true,
                                            })
                                        }
                                        value={String(form.watch(formFieldName))}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={formatMessage(formFieldBlockName)} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(selectOptions).map(([key, value]) => {
                                                return (
                                                    <SelectItem key={key} value={value}>
                                                        {key}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
        );
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlockTitle className="font-bold">
                {formatMessage(parkingManagementMessages.generalSettings)}
            </FormFieldBlockTitle>
            <FormFieldBlockSeparator />
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(generalMessages.name)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input type="text" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
            <FormFieldBlockSeparator />
            {getFormFieldBlock('address', generalMessages.address)}
            <FormFieldBlockSeparator />
            {getFormFieldBlock('system_id', parkingManagementMessages.systemID)}
            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.city)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={formatMessage(parkingManagementMessages.city)} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {cityList?.results.map((city: City) => (
                                                <SelectItem key={city.id} value={city.id}>
                                                    {city.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.geoType)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name="geo_type"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={formatMessage(parkingManagementMessages.geoType)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.values(GEO_TYPES).map((geo_type_option: GEO_TYPES) => {
                                                const label = geoTypeOptions.find(
                                                    (geo_type) => geo_type.value === geo_type_option,
                                                )?.label;
                                                const translatedLabel: string = label
                                                    ? formatMessage(label)
                                                    : field.value;

                                                return (
                                                    <SelectItem key={geo_type_option} value={geo_type_option}>
                                                        {translatedLabel}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.geolocation)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                {form.watch('geo_type') === GEO_TYPES.POINT && (
                    <FormFieldBlockBody className="w-full">
                        <FormField
                            control={form.control}
                            name="longitude"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <InputWithIcon
                                            icon={faX}
                                            inputMode="decimal"
                                            type="text"
                                            pattern="^-?\d*(\.?\d{0,16})?$"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="latitude"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <InputWithIcon
                                            icon={faY}
                                            inputMode="decimal"
                                            type="text"
                                            pattern="^-?\d*(\.?\d{0,16})?$"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </FormFieldBlockBody>
                )}

                {form.watch('geo_type') === GEO_TYPES.AREA && (
                    <FormFieldBlockBody className="w-full">
                        <FormField
                            control={form.control}
                            name="polygon"
                            render={({ field }) => (
                                <FormItem className="h-96 w-full">
                                    <FormControl>
                                        <Map
                                            initialViewState={{
                                                // Sofia coordinates
                                                latitude: 42.6977,
                                                longitude: 23.3219,
                                                zoom: 12,
                                            }}
                                            mapboxAccessToken={MAP_BOX_ACCESS_TOKEN}
                                            style={{ width: '100%', height: '100%' }}
                                            mapStyle="mapbox://styles/mapbox/streets-v11"
                                            onLoad={(e: any) => {
                                                const map = e.target;
                                                mapRef.current = map;

                                                const draw = new MapboxDraw({
                                                    displayControlsDefault: false,
                                                    controls: {
                                                        polygon: true,
                                                        trash: true,
                                                    },
                                                });
                                                drawRef.current = draw;

                                                map.addControl(draw);

                                                // Add the inital value (if available)
                                                if (field.value) {
                                                    if (data?.polygon?.type === 'MultiPolygon') {
                                                        // Split MultiPolygon into separate Polygon features
                                                        data.polygon.coordinates.forEach((coords: any, i: number) => {
                                                            draw.add({
                                                                id: `polygon-${i}`,
                                                                type: 'Feature',
                                                                properties: {},
                                                                geometry: {
                                                                    type: 'Polygon',
                                                                    coordinates: coords,
                                                                },
                                                            });
                                                        });
                                                    }
                                                }
                                                const syncPolygons = () => {
                                                    const all = draw.getAll();

                                                    const polygons = all.features
                                                        .filter((f: any) => f.geometry.type === 'Polygon')
                                                        .map((f: any) => f.geometry);
                                                    field.onChange(polygons);
                                                };

                                                map.on('draw.create', syncPolygons);
                                                map.on('draw.update', syncPolygons);
                                                map.on('draw.delete', syncPolygons);

                                                const initialColor =
                                                    form.getValues('area_color') || defaultValues.area_color;
                                                updatePolygonColors(initialColor);
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </FormFieldBlockBody>
                )}
            </FormFieldBlock>

            {form.watch('geo_type') === GEO_TYPES.AREA && (
                <>
                    <FormFieldBlockSeparator />

                    <FormFieldBlock>
                        <FormFieldBlockLabel>
                            <FormFieldBlockTitle>
                                <FormFieldBlockName>
                                    {formatMessage(parkingManagementMessages.areaColor)}
                                </FormFieldBlockName>
                            </FormFieldBlockTitle>
                        </FormFieldBlockLabel>
                        <FormFieldBlockBody className="w-full">
                            <FormField
                                control={form.control}
                                name="area_color"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                type="color"
                                                {...field}
                                                value={field.value || defaultValues.area_color}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    updatePolygonColors(e.target.value);
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </FormFieldBlockBody>
                    </FormFieldBlock>
                </>
            )}

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>
                            {formatMessage(parkingManagementMessages.defaultCurrency)}
                        </FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name="default_currency"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={formatMessage(parkingManagementMessages.defaultCurrency)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.values(CURRENCIES).map((currency: CURRENCIES) => (
                                                <SelectItem key={currency} value={currency}>
                                                    {currency}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            {getFormFieldSelect('is_public', parkingManagementMessages.publicParking, parkingSelectMapping)}
            <FormFieldBlockSeparator />
            {getFormFieldSelect('is_featured', parkingManagementMessages.featuredParking, parkingSelectMapping)}
            <FormFieldBlockSeparator />

            {getFormFieldSelect(
                'enable_online_reservations',
                reservationMessages.reservations,
                reservationsPaymentsSelectMapping,
            )}
            <FormFieldBlockSeparator />
            {getFormFieldSelect(
                'enable_online_payments',
                parkingManagementMessages.onlinePayments,
                reservationsPaymentsSelectMapping,
            )}

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(cashCollectionsMessages.cashCollection)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <Select
                        disabled={isStatusLoading}
                        onValueChange={setNewCollectionStatus}
                        value={newCollectionStatus ?? cashStatus?.is_enabled.toString()}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Object.entries(reservationsPaymentsSelectMapping).map(([key, value]) => {
                                return (
                                    <SelectItem key={key} value={value}>
                                        {key}
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                </FormFieldBlockBody>
            </FormFieldBlock>
            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.roles)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="roles"
                        render={({ field }) => {
                            const { ref: _, ...rest } = field;
                            const _fieldValue = Array.isArray(field.value) ? field.value : [field.value];
                            const fieldData: string = _fieldValue
                                ?.map((role: string): string | null => {
                                    const label = membershipRoleOptions.find((option) => option.value === role)?.label;
                                    return label ? formatMessage(label) : null;
                                })
                                .filter((label: string | null): label is string => typeof label === 'string')
                                .join(', ');

                            const allValues: string[] = rolesList
                                .map((role: string): string | undefined => {
                                    return membershipRoleOptions.find((option): boolean => option.value === role)
                                        ?.value;
                                })
                                .filter((item) => item !== undefined);

                            return (
                                <FormItem>
                                    <FormControlStack>
                                        <FormControl>
                                            <MultiSelect {...rest}>
                                                <MultiSelectTrigger asChild>
                                                    <MultiSelectControl className="w-full">
                                                        {fieldData.length > 0 ? (
                                                            fieldData
                                                        ) : (
                                                            <span className="text-gray-400">
                                                                <FormattedMessage
                                                                    {...parkingManagementMessages.roles}
                                                                />
                                                            </span>
                                                        )}
                                                    </MultiSelectControl>
                                                </MultiSelectTrigger>
                                                <MultiSelectContent allValues={allValues}>
                                                    {rolesList.map((role: string) => {
                                                        const label = membershipRoleOptions.find(
                                                            (recipient) => recipient.value === role,
                                                        )?.label;
                                                        const translatedLabel = label ? formatMessage(label) : '-';
                                                        return (
                                                            <MultiSelectOption key={role} selected={role}>
                                                                {translatedLabel}
                                                            </MultiSelectOption>
                                                        );
                                                    })}
                                                </MultiSelectContent>
                                            </MultiSelect>
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            );
                        }}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormField
                control={form.control}
                name="photo"
                render={({ field }) => {
                    const photoValue = form.watch('photo');
                    return (
                        <FormItem>
                            <ImageUpload
                                onChange={field.onChange}
                                imagePreview={
                                    typeof photoValue === 'string'
                                        ? photoValue
                                        : photoValue instanceof File
                                          ? URL.createObjectURL(photoValue)
                                          : defaultValues.photoPreview
                                }
                            />
                        </FormItem>
                    );
                }}
            />
            <FormFieldBlockSeparator />

            <div className="flex justify-end">
                <Button
                    className="w-full sm:w-auto"
                    type="submit"
                    disabled={!isDirty && newCollectionStatus === cashStatus?.is_enabled?.toString()}
                >
                    {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default GeneralSettings;
